import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  ResponsiveChartContainer,
  LinePlot,
  ChartsYAxis,
  ChartsTooltip,
  MarkPlot,
} from "@mui/x-charts";
import Paper from "@mui/material/Paper";
import { Box } from "@mui/material";

import CustomTooltip from "./CustomTooltip";
import DrawingCost from "./DrawingCost";
import DrawingMargin from "./DrawingMargin";
import SeriesLabel from "../../Components/SeriesLabel";
import DateRangePicker from "../../Components/DateRangePicker";
import ChartLoading from "../ChartLoading";
import SwitchButtons from "../SwitchButtons";
import Table from "./Table";

import { useLazyQuery } from "@apollo/client";
import { MARGIN_HISTORY } from "@/services/productService";

import { Colors } from "@/theme";
import { getLocalisationVal } from "@/utils/localizationUtil";
import useStyles from "./styles";
import { MERCHANT_ID } from "@/constants/env";
import useUpdateEffect from "@/hooks/useUpdateEffect";

const CHART_HEIGHT = 400;

const ChartMargin = ({
  store,
  product,
  localization,
  defaultDateRange,
  marginData,
  onChangeMarginData,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [isChart, setIsChart] = useState(true);

  const [hasLoaded, setHasLoaded] = useState(true);
  const [dateRange, setDateRange] = useState(defaultDateRange);

  const [getMarginHistory] = useLazyQuery(MARGIN_HISTORY, {
    onCompleted: (data) => {
      onChangeMarginData(data.margin_history?.[0]);
      setHasLoaded(true);
    },
    onError: () => setHasLoaded(true),
    fetchPolicy: "no-cache",
  });

  useUpdateEffect(() => {
    setHasLoaded(false);
    getMarginHistory({
      context: {
        headers: {
          merchantId: MERCHANT_ID,
          storeId: store.id,
        },
      },
      variables: {
        product_ids: [product.id],
        from: moment(dateRange[0]).toISOString().replace("Z", ""),
        to: moment(dateRange[1]).toISOString().replace("Z", ""),
      },
    });
  }, [dateRange]);

  const config = {
    sx: {
      "& .MuiChartsAxis-root .MuiChartsAxis-line": {
        stroke: Colors.GRAY_BORDER,
      },
      "& .MuiChartsAxis-root .MuiChartsAxis-tick": {
        stroke: Colors.GRAY_BORDER,
      },
      "& .MuiLineElement-root": {
        strokeWidth: 4,
      },
    },
    xAxis: [
      {
        data: marginData.map((margin) => moment(margin.time)),
        scaleType: "time",
      },
    ],
    yAxis: [
      {
        id: "yCost",
        tickLabelStyle: {
          display: "none",
        },
      },
      {
        id: "yMargin",
        tickLabelStyle: {
          display: "none",
        },
      },
    ],
    series: [
      {
        id: "margin",
        type: "line",
        data: marginData.map((margin) =>
          parseFloat(getLocalisationVal(localization, margin.absolute_margin))
        ),
        yAxisKey: "yMargin",
        color: Colors.GREEN_DARK,
        curve: "linear",
        label: "Margin",
      },
      {
        id: "cost",
        type: "line",
        data: marginData.map((margin) =>
          parseFloat(getLocalisationVal(localization, margin.buy_price))
        ),
        yAxisKey: "yCost",
        color: Colors.GREEN,
        curve: "linear",
        label: "Cost",
      },
      {
        id: "sell",
        type: "line",
        data: marginData.map((margin) =>
          parseFloat(getLocalisationVal(localization, margin.sell_price))
        ),
        yAxisKey: "yCost",
        color: Colors.BLUE,
        curve: "linear",
        label: "Cost",
      },
    ],
    height: CHART_HEIGHT,
  };

  return (
    <Paper
      sx={{
        borderRadius: 4,
        position: "relative",
        boxShadow: "none",
        marginBottom: 15,
        borderWidth: 1,
        borderColor: Colors.GRAY_BORDER,
        borderStyle: "solid",
      }}
    >
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingTop={5}
        paddingX={11}
      >
        <span className={classes.codeText}>{product.product_code}</span>
        <Box display={"flex"} alignItems={"center"}>
          <DateRangePicker
            dateRange={dateRange}
            onChangeDateRange={setDateRange}
          />
          <Box ml={15}>
            <SwitchButtons isChart={isChart} onSwitch={setIsChart} />
          </Box>
        </Box>
      </Box>
      {isChart ? (
        hasLoaded ? (
          <>
            <ResponsiveChartContainer {...config}>
              <LinePlot />
              <ChartsYAxis
                position="left"
                axisId="yCost"
                disableTicks
                label={t("dashboard_screen.price")}
                labelStyle={{ fontSize: 20 }}
              />
              <ChartsYAxis
                position="right"
                axisId="yMargin"
                disableTicks
                label={t("dashboard_screen.margin")}
                labelStyle={{ fontSize: 20 }}
              />
              <ChartsTooltip
                slots={{ itemContent: CustomTooltip }}
                trigger="item"
              />
              <MarkPlot />
              {/* <DrawingCost localization={localization} costData={costData} />
              <DrawingMargin
                localization={localization}
                marginData={marginData}
              /> */}
            </ResponsiveChartContainer>

            <Box display={"flex"} justifyContent={"center"} pb={6}>
              <SeriesLabel
                color={Colors.GREEN}
                label={t("dashboard_screen.cost_price")}
              />
              <Box ml={10}>
                <SeriesLabel
                  color={Colors.BLUE}
                  label={t("dashboard_screen.sell_price")}
                />
              </Box>
              <Box ml={10}>
                <SeriesLabel
                  color={Colors.GREEN_DARK}
                  label={t("dashboard_screen.margin")}
                />
              </Box>
            </Box>
          </>
        ) : (
          <Box paddingX={11} paddingY={5}>
            <ChartLoading height={400} />
          </Box>
        )
      ) : (
        <Table loading={!hasLoaded} marginData={marginData} />
      )}
    </Paper>
  );
};

export default ChartMargin;
